// The one list of device types, models and control traits for the dashboard.
// Mirrors the HASHh Connect app (checked against android/dev/4.1.11): the types in
// app/theme/DeviceSpecific.js and app/components/SwitchComponent.js, the models
// in app/assets/json/hashProductList.js and the traits used in
// app/screens/CommonSwichDetails.js. Import from here instead of writing type
// or model strings in components.

// Device types as the API reports them in config device.deviceType.
export const DEVICE_TYPES = Object.freeze({
  LIGHT: "LIGHT",
  SWITCH: "SWITCH",
  SOCKET: "SOCKET",
  FAN: "FAN",
  MOTOR: "MOTOR",
  RGB_LIGHT: "RGB_LIGHT",
  DIMMER_MODULE: "DIMMER_MODULE",
  CURTAIN_MODULE: "CURTAIN_MODULE",
  WATER_LEVEL_CONTROLLER: "WATER_LEVEL_CONTROLLER",
  SLIDING_GATE: "SLIDING_GATE",
  SWING_GATE: "SWING_GATE",
  SHUTTER_GATE: "SHUTTER_GATE",
  TURNING_SLIDING_GATE: "TURNING_SLIDING_GATE",
  DW_SENSOR: "DW_SENSOR",
  PIR_SENSOR: "PIR_SENSOR",
  GAS_SENSOR: "GAS_SENSOR",
  SMOKE_DETECTOR: "SMOKE_DETECTOR",
  DOOR_BELL: "DOOR_BELL",
  SMART_LOCK: "SMART_LOCK",
  IRBM: "IRBM",
  ROBOT_VACUUM_CLEANER: "ROBOT_VACUUM_CLEANER",
  SECURITY_ALARM: "SECURITY_ALARM",
  ENERGY_METER: "ENERGY_METER",
});

const T = DEVICE_TYPES;

// Remotes learned on an IR blaster (IRBM device.remoteDevices[].deviceType).
export const REMOTE_TYPES = Object.freeze({
  AC: "AC",
  TV: "TV",
  STB: "STB",
  DVD: "DVD",
  STEREO: "STEREO",
  PROJECTOR: "PROJECTOR",
});

// Device models (device.meta.model).
export const MODELS = Object.freeze({
  // Wi-Fi switch modules that take trait 101; the type is set per channel.
  W1SM_1: "W1SM_1",
  W2SM_1: "W2SM_1",
  W3SM_1: "W3SM_1",
  W4SM_1: "W4SM_1",
  W1TSM_1: "W1TSM_1",
  W2TSM_1: "W2TSM_1",
  W3TSM_1: "W3TSM_1",
  W4TSM_1: "W4TSM_1",
  W8SM_1: "W8SM_1",
  W10SM_1: "W10SM_1",
  W10SM_2: "W10SM_2",
  W16SM_1: "W16SM_1",
  W1F6SM_1: "W1F6SM_1",
  // Legacy switch modules.
  PL10_1: "10PL_1",
  PL10_1_1: "10PL_1_1",
  PL10_1_2: "10PL_1_2",
  PRL7_1: "7PRL_1",
  PRL7_1_1: "7PRL_1_1",
  PRL7_1_2: "7PRL_1_2",
  PL3_1: "3PL_1",
  DL1_1: "1DL_1",
  MSB_1: "MSB_1",
  // Plugs.
  W1SP_1: "W1SP_1",
  ISP_1: "ISP_1",
  // RGB.
  SBRGBCW_1: "SBRGBCW_1",
  AL_1: "AL_1",
  // Sensors, bell and lock.
  ISDW_1: "ISDW_1",
  ISDW_2: "ISDW_2",
  WPCS_1: "WPCS_1",
  ISMD_1: "ISMD_1",
  ISGS_1: "ISGS_1",
  ISSD_1: "ISSD_1",
  DB_1: "DB_1",
  WFSL_1: "WFSL_1",
  // IR blasters.
  IRBM_1: "IRBM_1",
  IRBM_2: "IRBM_2",
  // Controllers.
  GC_1: "GC_1",
  WGC_1: "WGC_1",
  WGC_2: "WGC_2",
  WLC_1: "WLC_1",
  W1CM_1: "W1CM_1",
  W2CM_1: "W2CM_1",
  W1DM_1: "W1DM_1",
  W2DM_1: "W2DM_1",
  WGRVC_1: "WGRVC_1",
  WGRVC_2: "WGRVC_2",
  WZLTEG_1: "WZLTEG_1",
  W1EMP_1: "W1EMP_1",
  W3EMP_1: "W3EMP_1",
});

const M = MODELS;

// The type each single-purpose model has, for devices that report no
// deviceType. Switch modules and gate controllers are left out: each channel
// or gate has its own type.
export const MODEL_TYPE = Object.freeze({
  [M.W1SP_1]: T.SOCKET,
  [M.ISP_1]: T.SOCKET,
  [M.SBRGBCW_1]: T.RGB_LIGHT,
  [M.AL_1]: T.RGB_LIGHT,
  [M.MSB_1]: T.MOTOR,
  [M.ISDW_1]: T.DW_SENSOR,
  [M.ISDW_2]: T.DW_SENSOR,
  [M.WPCS_1]: T.DW_SENSOR,
  [M.ISMD_1]: T.PIR_SENSOR,
  [M.ISGS_1]: T.GAS_SENSOR,
  [M.ISSD_1]: T.SMOKE_DETECTOR,
  [M.DB_1]: T.DOOR_BELL,
  [M.WFSL_1]: T.SMART_LOCK,
  [M.IRBM_1]: T.IRBM,
  [M.IRBM_2]: T.IRBM,
  [M.WLC_1]: T.WATER_LEVEL_CONTROLLER,
  [M.W1CM_1]: T.CURTAIN_MODULE,
  [M.W2CM_1]: T.CURTAIN_MODULE,
  [M.W1DM_1]: T.DIMMER_MODULE,
  [M.W2DM_1]: T.DIMMER_MODULE,
  [M.WGRVC_1]: T.ROBOT_VACUUM_CLEANER,
  [M.WGRVC_2]: T.ROBOT_VACUUM_CLEANER,
  [M.WZLTEG_1]: T.SECURITY_ALARM,
  [M.W1EMP_1]: T.ENERGY_METER,
  [M.W3EMP_1]: T.ENERGY_METER,
});

// Control traits (supportedDeviceTraits holds them as strings).
export const TRAITS = Object.freeze({
  ON_OFF: "101",
  POWER_PROTECTION: "106",
  POWER_SETTINGS: "107",
  LAST_STATE: "108",
  FAN_SPEED: "201",
  ARM: "401",
  GATE: "601",
  MOTOR: "701",
  MOTOR_SETTINGS: "702",
  MOTOR_FAULT_CLEAR: "704",
  MOTOR_MANUAL: "705",
  CURTAIN: "901",
  CURTAIN_TRAVEL_TIME: "902",
  DIMMER_RANGE: "1002",
  DIMMER: "1003",
  VACUUM_POWER: "1101",
  VACUUM_MODE: "1103",
  VACUUM_SUCTION: "1106",
  VACUUM_VOLUME: "1109",
  // Security alarm gateway (WZLTEG_1). 1301 ids: 1 mode (0 Arm Away, 1 Stay
  // Arm, 2 Disarm), 2 SOS, 3 electrical facility, 4 accessory pairing,
  // 5 refresh the system info.
  SECURITY_MODE: "1301",
  SECURITY_ACCESSORY: "1303",
  SECURITY_REMOTE: "1304",
  SECURITY_SETTINGS: "1305",
  SECURITY_NUMBERS: "1306",
  METER_RELAY: "1401",
  // Energy meter settings: 1402 holds the reclosing, delay and relay
  // settings, 1403 the alarm limits and the voltage and current multipliers.
  METER_SETTINGS: "1402",
  METER_ALARMS: "1403",
});

// Capability names in device.traits.
export const TRAIT_NAMES = Object.freeze({
  FAN_SPEED: "FanSpeed",
  BRIGHTNESS: "Brightness",
  COLOR: "ColorSetting",
  EFFECTS: "LightEffects",
  REMOTE: "Remote",
});

// Groups.
export const GATE_TYPES = Object.freeze([T.SLIDING_GATE, T.SWING_GATE, T.SHUTTER_GATE, T.TURNING_SLIDING_GATE]);
export const SENSOR_TYPES = Object.freeze([T.DW_SENSOR, T.PIR_SENSOR, T.GAS_SENSOR, T.SMOKE_DETECTOR, T.DOOR_BELL, T.SMART_LOCK]);
// Sensors whose icon follows isPresent.
export const PRESENCE_TYPES = Object.freeze([T.PIR_SENSOR, T.GAS_SENSOR, T.SMOKE_DETECTOR]);
export const CONTROLLER_TYPES = Object.freeze([...GATE_TYPES, T.WATER_LEVEL_CONTROLLER, T.CURTAIN_MODULE]);
// Types a card switches from its icon, as in the app. Everything else opens
// its details, so a stray click can't move a gate or start a vacuum.
export const CARD_TOGGLE_TYPES = Object.freeze([T.LIGHT, T.SOCKET, T.FAN, T.SWITCH, T.MOTOR, T.RGB_LIGHT, T.DIMMER_MODULE]);
// Types the user can switch between on the Edit Device page.
export const EDITABLE_TYPES = Object.freeze([T.LIGHT, T.SWITCH, T.SOCKET]);
// Types whose type can't be changed on the Edit Device page.
export const FIXED_TYPES = Object.freeze([
  T.DW_SENSOR, T.IRBM, T.PIR_SENSOR, T.GAS_SENSOR, T.SMOKE_DETECTOR, T.WATER_LEVEL_CONTROLLER,
  T.CURTAIN_MODULE, T.DIMMER_MODULE, T.SMART_LOCK, T.ROBOT_VACUUM_CLEANER, T.SECURITY_ALARM, T.FAN, T.ENERGY_METER,
]);
// Switch modules that take the trait 101 on/off action.
export const ON_OFF_ACTION_MODELS = Object.freeze([
  M.W1SM_1, M.W2SM_1, M.W3SM_1, M.W4SM_1, M.W1TSM_1, M.W2TSM_1, M.W3TSM_1, M.W4TSM_1,
  M.W8SM_1, M.W10SM_1, M.W10SM_2, M.W16SM_1, M.W1F6SM_1, M.W1SP_1,
]);
export const GATE_CONTROLLER_MODELS = Object.freeze([M.GC_1, M.WGC_1, M.WGC_2]);
// Models that report a battery level.
export const BATTERY_MODELS = Object.freeze([M.ISDW_2, M.ISMD_1, M.ISSD_1]);
// Plugs that measure power (energy stats instead of run time).
export const METERING_PLUG_MODELS = Object.freeze([M.ISP_1, M.W1SP_1]);

// Model lists used to pick a device's card and detail page.
export const SENSORS = Object.freeze([M.ISDW_1, M.ISDW_2, M.WPCS_1, M.ISMD_1, M.ISGS_1, M.ISSD_1, M.DB_1, M.WFSL_1]);
export const CONTROLLERS = Object.freeze([M.GC_1, M.WGC_1, M.WGC_2, M.WLC_1, M.W1CM_1, M.W2CM_1]);
export const SECURITY_DEVICES = Object.freeze([M.WZLTEG_1]);
export const ENERGY_METERS = Object.freeze([M.W1EMP_1, M.W3EMP_1]);

// ---------- Helpers ----------

export const modelOf = (device) => device?.meta?.model ?? null;

// A device's type, as the app reads it: device.deviceType, or the model's type
// when the device has none.
export const typeOf = (device) => device?.deviceType?.toUpperCase() || MODEL_TYPE[modelOf(device)] || null;

export const isType = (device, ...types) => types.includes(typeOf(device));
export const isSensor = (device) => SENSOR_TYPES.includes(typeOf(device));
export const isGate = (device) => GATE_TYPES.includes(typeOf(device));
export const isModel = (device, ...models) => models.includes(modelOf(device));

export const hasTrait = (device, trait) => Boolean(device?.supportedDeviceTraits?.includes(trait));
export const hasTraitName = (device, name) =>
  Boolean(device?.traits?.some((trait) => (typeof trait === "string" ? trait : trait?.name) === name));

// Which card and detail page a device gets.
export const CARD_KINDS = Object.freeze({
  SWITCH: "switch",
  SENSOR: "sensor",
  CONTROLLER: "controller",
  SECURITY: "security",
  ENERGY: "energy",
});

export const cardKindOf = (device) => {
  const type = typeOf(device);
  if (SENSOR_TYPES.includes(type)) return CARD_KINDS.SENSOR;
  if (CONTROLLER_TYPES.includes(type)) return CARD_KINDS.CONTROLLER;
  if (type === T.SECURITY_ALARM) return CARD_KINDS.SECURITY;
  if (type === T.ENERGY_METER) return CARD_KINDS.ENERGY;
  return CARD_KINDS.SWITCH;
};

// Lower-case key used by the colour and icon tables (app/theme/DeviceSpecific.js).
export const themeKey = (typeOrDevice) =>
  (typeof typeOrDevice === "string" ? typeOrDevice : typeOf(typeOrDevice))?.toLowerCase() ?? "";

// Readable name for a type, e.g. "Door sensor" for DW_SENSOR.
const TYPE_LABELS = {
  [T.DW_SENSOR]: "Door sensor",
  [T.PIR_SENSOR]: "Motion sensor",
  [T.GAS_SENSOR]: "Gas sensor",
  [T.SMOKE_DETECTOR]: "Smoke detector",
  [T.DOOR_BELL]: "Door bell",
  [T.SMART_LOCK]: "Smart lock",
  [T.IRBM]: "IR blaster",
  [T.RGB_LIGHT]: "RGB light",
  [T.DIMMER_MODULE]: "Dimmer",
  [T.CURTAIN_MODULE]: "Curtain",
  [T.WATER_LEVEL_CONTROLLER]: "Water level controller",
  [T.ROBOT_VACUUM_CLEANER]: "Robot vacuum",
  [T.SECURITY_ALARM]: "Security alarm",
  [T.ENERGY_METER]: "Energy meter",
};
export const typeLabel = (type) =>
  TYPE_LABELS[type] ?? (type ? type.charAt(0) + type.slice(1).toLowerCase().replace(/_/g, " ") : "");
